import { useEffect, useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import {
  borderGrey,
  borderPrimary,
  darkerBG,
  mediumText,
  primaryText,
  secondaryText,
  semiBoldText,
  whiteBG,
} from '../../../../../../styling/stylingHelper';
import { InventorySlideOver, slideOverCancelClass, slideOverPrimaryClass } from '../../../../../ui/InventorySlideOver';
import Toggle from '../../../../../ui/Toggle';
import { saveBorkConnection, type AdminRestaurant, type BorkConnection } from '../../../../../../services/admin/adminService';
import type { TFn } from '../../types';

interface Props {
  open: boolean;
  /** The connection being edited; null opens the drawer in "add" mode. */
  connection: BorkConnection | null;
  restaurants: AdminRestaurant[];
  /** Restaurants that already own a connection — hidden from the add-mode dropdown. */
  takenRestaurantIds: string[];
  onClose: () => void;
  onSaved: () => void;
  t: TFn;
}

/**
 * The routing exceptions as an operator edits them: one `Category = SOURCE_TYPE` per line, which is
 * how they read off the report ("Hotel goes to HOTEL"). A non-blank line that is not that shape comes
 * back as invalid rather than being skipped — silently dropping it would leave the operator believing
 * they routed a category they did not. The source type itself is checked server-side, which answers
 * a typo with a 400 that lands in the error box.
 */
function parseSourceTypeOverrides(text: string): { overrides: Record<string, string>; invalidLine: string | null } {
  const overrides: Record<string, string> = {};
  for (const rawLine of text.split('\n')) {
    if (!rawLine.trim()) continue;
    const [name, sourceType, ...rest] = rawLine.split('=');
    if (!name.trim() || !sourceType?.trim() || rest.length > 0) return { overrides, invalidLine: rawLine.trim() };
    overrides[name.trim()] = sourceType.trim().toUpperCase();
  }
  return { overrides, invalidLine: null };
}

const formatSourceTypeOverrides = (overrides: Record<string, string> | undefined): string =>
  Object.entries(overrides ?? {})
    .map(([name, sourceType]) => `${name} = ${sourceType}`)
    .join('\n');

/**
 * Add / edit one Bork direct-API connection (a restaurant's own trivecgateway base URL plus the
 * group-wide appid). The appid is write-only: the list only ever holds its last 4 characters, so
 * leaving the field blank on an edit keeps whatever is stored rather than clearing it.
 */
export function BorkConnectionSlideOver({ open, connection, restaurants, takenRestaurantIds, onClose, onSaved, t }: Props) {
  const [restaurantId, setRestaurantId] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [appId, setAppId] = useState('');
  const [centerName, setCenterName] = useState('');
  const [sourceTypeOverrides, setSourceTypeOverrides] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed the draft every time the drawer opens so a cancelled edit never leaks into the next one.
  useEffect(() => {
    if (!open) return;
    setRestaurantId(connection?.restaurantId ?? '');
    setBaseUrl(connection?.baseUrl ?? '');
    setAppId('');
    setCenterName(connection?.centerName ?? '');
    setSourceTypeOverrides(formatSourceTypeOverrides(connection?.sourceTypeOverrides));
    setIsEnabled(connection?.isEnabled ?? false);
    setError(null);
  }, [open, connection]);

  const isEdit = !!connection;
  const canSave = !!restaurantId && !!baseUrl.trim() && (isEdit || !!appId.trim());

  const save = async () => {
    if (!canSave) return;
    try {
      const { overrides, invalidLine } = parseSourceTypeOverrides(sourceTypeOverrides);
      if (invalidLine) {
        setError(t('adminTesting.bork.sourceTypeOverridesInvalid', { line: invalidLine }));
        return;
      }
      setSaving(true);
      setError(null);
      await saveBorkConnection(restaurantId, {
        baseUrl: baseUrl.trim(),
        ...(appId.trim() ? { appId: appId.trim() } : {}),
        centerName: centerName.trim() || null,
        sourceTypeOverrides: overrides,
        isEnabled,
      });
      onSaved();
    } catch (e: any) {
      setError(e.message || 'Failed to save connection');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = cn('w-full px-[13px] py-[11px] text-[14px] leading-5 border rounded-xl focus:outline-none', semiBoldText, borderPrimary, whiteBG, primaryText);
  const labelClass = cn(secondaryText, mediumText, 'mb-2 text-[15px] leading-6 block');
  const availableRestaurants = restaurants.filter((r) => r.id === connection?.restaurantId || !takenRestaurantIds.includes(r.id));

  return (
    <InventorySlideOver
      isOpen={open}
      onClose={onClose}
      title={isEdit ? t('adminTesting.bork.editConnectionTitle') : t('adminTesting.bork.addConnectionTitle')}
      description={t('adminTesting.bork.connectionFormDesc')}
      closeLabel={t('adminTesting.bork.close')}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={saving} className={slideOverCancelClass}>
            {t('adminTesting.bork.cancel')}
          </button>
          <button type="button" onClick={save} disabled={saving || !canSave} className={slideOverPrimaryClass}>
            {saving ? t('adminTesting.bork.saving') : t('adminTesting.bork.save')}
          </button>
        </>
      }
    >
      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className={cn('space-y-4 p-4 rounded-lg border', borderGrey, darkerBG)}>
        <div>
          <label className={labelClass} htmlFor="bork-restaurant">{t('adminTesting.bork.restaurantLabel')}</label>
          {isEdit ? (
            <div className={cn(inputClass, 'truncate')}>{connection?.restaurantName ?? connection?.name}</div>
          ) : (
            <select id="bork-restaurant" value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)} className={inputClass}>
              <option value="">{t('adminTesting.bork.restaurantPlaceholder')}</option>
              {availableRestaurants.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className={labelClass} htmlFor="bork-base-url">{t('adminTesting.bork.baseUrlLabel')}</label>
          <input
            id="bork-base-url"
            type="text"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://XXXXXXXX.trivecgateway.com"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="bork-app-id">{t('adminTesting.bork.appIdLabel')}</label>
          <input
            id="bork-app-id"
            type="password"
            autoComplete="new-password"
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            placeholder={isEdit ? t('adminTesting.bork.appIdKeepPlaceholder', { masked: connection?.appIdMasked ?? '—' }) : '••••••••'}
            className={inputClass}
          />
          <p className={cn(secondaryText, 'text-xs mt-1')}>
            {isEdit ? t('adminTesting.bork.appIdEditHelp') : t('adminTesting.bork.appIdHelp')}
          </p>
        </div>

        <div>
          <label className={labelClass} htmlFor="bork-center">{t('adminTesting.bork.centerNameLabel')}</label>
          <input
            id="bork-center"
            type="text"
            value={centerName}
            onChange={(e) => setCenterName(e.target.value)}
            placeholder={t('adminTesting.bork.centerNamePlaceholder')}
            className={inputClass}
          />
          <p className={cn(secondaryText, 'text-xs mt-1')}>{t('adminTesting.bork.centerNameHelp')}</p>
        </div>

        <div>
          <label className={labelClass} htmlFor="bork-source-type-overrides">
            {t('adminTesting.bork.sourceTypeOverridesLabel')}
          </label>
          <textarea
            id="bork-source-type-overrides"
            rows={3}
            value={sourceTypeOverrides}
            onChange={(e) => setSourceTypeOverrides(e.target.value)}
            placeholder={t('adminTesting.bork.sourceTypeOverridesPlaceholder')}
            className={cn(inputClass, 'font-mono text-[13px] resize-y')}
          />
          <p className={cn(secondaryText, 'text-xs mt-1')}>{t('adminTesting.bork.sourceTypeOverridesHelp')}</p>
        </div>

        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <span className={cn(labelClass, 'mb-1')}>{t('adminTesting.bork.enabledLabel')}</span>
            <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.bork.enabledHelp')}</p>
          </div>
          <Toggle
            checked={isEnabled}
            onChange={setIsEnabled}
            disabled={saving}
            ariaLabel={t('adminTesting.bork.enabledLabel')}
          />
        </div>
      </div>
    </InventorySlideOver>
  );
}
